import React from "react";
import styled from "styled-components";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Button, Modal, ModalBody, ModalFooter, FormGroup, Label, Input, FormFeedback } from "reactstrap";

const validationSchema = Yup.object().shape({
    first_name: Yup.string()
        .trim()
        .required("First name is required"),
    last_name: Yup.string()
        .trim()
        .required("Last name is required"),
    email: Yup.string()
        .email("Invalid email format")
        .required("Email is required"),
    password: Yup.string()
        .min(8, "Password must be at least 8 characters")
        .required("Password is required"),
    ward: Yup.string()
        .trim()
});

const AddUserModal = ({ isOpen, toggle, isLoading, handleSubmit }) => {
    const initialValues = {
        first_name: "",
        last_name: "",
        email: "",
        password: "",
        ward: "",
    };

    const onSubmit = (values, { setSubmitting }) => {
        handleSubmit(values);
        setSubmitting(false);
    };

    return (
        <ModalStyled 
            isOpen={isOpen} 
            toggle={toggle} 
            backdrop={`static`} 
            centered
        >
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
            >
                {({ isSubmitting, errors }) => (
                    <Form>
                        <ModalBody>
                            <div className="modal-header-custom">
                                <div className="title-modal">Add New User</div>
                            </div>
                            <div className="modal-description mt-2 mb-4">
                                Create a new user account. Fill in the required information below.
                            </div>
                            <FormGroup>
                                <Label className="ms-label" for="first_name">
                                    First Name <span className="text-danger">*</span>
                                </Label>
                                <Field
                                    type="text"
                                    name="first_name"
                                    id="first_name"
                                    placeholder="Enter first name"
                                    as={Input}
                                    invalid={!!errors.first_name}
                                />
                                <ErrorMessage name="first_name" component={FormFeedback} className="d-block" />
                            </FormGroup>
                            <FormGroup>
                                <Label className="ms-label" for="last_name">
                                    Last Name <span className="text-danger">*</span>
                                </Label>
                                <Field
                                    type="text"
                                    name="last_name"
                                    id="last_name"
                                    placeholder="Enter last name"
                                    as={Input}
                                    invalid={!!errors.last_name}
                                />
                                <ErrorMessage name="last_name" component={FormFeedback} className="d-block" />
                            </FormGroup>
                            <FormGroup>
                                <Label className="ms-label" for="email">
                                    Email <span className="text-danger">*</span>
                                </Label>
                                <Field
                                    type="email"
                                    name="email"
                                    id="email"
                                    placeholder="user@example.com"
                                    as={Input}
                                    invalid={!!errors.email}
                                />
                                <ErrorMessage name="email" component={FormFeedback} className="d-block" />
                            </FormGroup>
                            <FormGroup>
                                <Label className="ms-label" for="password">
                                    Password <span className="text-danger">*</span>
                                </Label>
                                <Field
                                    type="password"
                                    name="password"
                                    id="password"
                                    placeholder="Enter password"
                                    as={Input}
                                    invalid={!!errors.password}
                                />
                                <ErrorMessage name="password" component={FormFeedback} className="d-block" />
                            </FormGroup>
                            <FormGroup>
                                <Label className="ms-label" for="ward">
                                    Ward
                                </Label>
                                <Field
                                    type="text"
                                    name="ward"
                                    id="ward"
                                    placeholder="Enter ward (e.g., Speech Therapist)"
                                    as={Input}
                                    invalid={!!errors.ward}
                                />
                                <ErrorMessage name="ward" component={FormFeedback} className="d-block" />
                            </FormGroup>
                        </ModalBody>
                        <ModalFooter>
                            <Button className="btn btn-secondary" disabled={isLoading || isSubmitting} onClick={toggle}>
                                Cancel
                            </Button>{" "}
                            <Button
                                type="submit"
                                className="btn btn-primary d-flex align-items-center"
                                disabled={isLoading || isSubmitting}
                            >
                                {isLoading && <span className="spinner-border spinner-border-sm mr-2"></span>}
                                Add User
                            </Button>
                        </ModalFooter>
                    </Form>
                )}
            </Formik>
        </ModalStyled>
    );
};

const ModalStyled = styled(Modal)`
    .modal-dialog {
        width: 512px;
        max-width: 512px;
        margin: 1.75rem auto;
    }
    
    .modal-content {
        width: 512px;
        border-radius: 8px;
    }
    
    .modal-body {
        padding: 16px 24px;
    }
    
    .modal-header-custom {
        margin-bottom: 12px;
    }
    
    .title-modal {
        font-size: 18px;
        font-weight: 600;
        color: #101828;
    }
    
    .modal-description {
        color: #344054;
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
    }
    
    .modal-footer {
        border-top: 1px solid #E5E7EB;
        padding: 12px 24px;
        justify-content: flex-end;
    }
    
    .ms-label {
        color: #344054;
        font-size: 14px;
        font-weight: 500;
        margin-bottom: 6px;
    }
    
    .form-control {
        height: 44px;
        border-radius: 8px;
        border: 1px solid #D0D5DD;
        font-size: 14px;
        color: #101828;
        
        &::placeholder {
            color: #98A2B3;
        }
        
        &:focus {
            border-color: #0E7AA8;
            box-shadow: 0 0 0 3px rgba(14, 122, 168, 0.1);
        }
    }
`;

export default AddUserModal;

